import type React from "react";
import { useCallback, useEffect, useState } from "react";
import "./BlurredImage.css";
import { FcNext, FcPrevious } from "react-icons/fc";
import { MdDone } from "react-icons/md";
import { RiFocus2Line } from "react-icons/ri";
import { BlurredImage } from "./BlurredImage";
import Button from "./Button";
import ScorePlate from "./ScorePlate";
import WordInput from "./WordInput";
import type { ItemType } from "./data";

type Props = {
	data: { word: string; image: string }[];
};

export const BlurredImageGame: React.FC<Props> = ({ data }) => {
	const [blur, setBlur] = useState(50);
	const [currentImage, setCurrentImage] = useState(0);
	const [wordsData, setWordsData] = useState<ItemType[]>([]);
	const [hint, setHint] = useState(false);
	const [, setAllSlotsFilled] = useState(false);
	const [word, setWord] = useState<string[]>([]);

	const imageIsClear = blur < 10;

	const handleImageBlur = () => {
		setBlur((prev) => prev - 10);
		setHint((prev) => !prev);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const newData = [...data];
		const d = newData.map((item) => {
			return {
				name: item.word,
				image: item.image,
				done: false,
				attempts: 4,
				maxPoints: 1000,
				blurred: 60,
				setAllSlotsFilled: false,
			};
		}) as unknown as ItemType[];
		setWordsData(d);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const reduceMaxPoints = useCallback(() => {
		const newPoints = [...wordsData];
		newPoints[currentImage] = {
			...newPoints[currentImage],
			maxPoints: newPoints[currentImage].maxPoints - 100,
		};
		setWordsData(newPoints);
	}, [hint]);

	const goToNextWord = () => {
		setCurrentImage((prev) => (prev + 1 < wordsData.length ? prev + 1 : prev));
		if (wordsData[currentImage].done) {
			setBlur(wordsData[currentImage].blurred);
		}
		setBlur(50);
	};

	const goToPrevWord = () => {
		setCurrentImage((prev) => (prev - 1 >= 0 ? prev - 1 : prev));
		if (wordsData[currentImage].done) {
			setBlur(wordsData[currentImage].blurred);
		}
		setBlur(50);
	};

	const checkAnswer = () => {
		const currentWord = wordsData[currentImage]?.name?.toLocaleLowerCase();
		const wordFromInput = word?.join("")?.toLocaleLowerCase();
		if (currentWord === wordFromInput) {
			const updatedWordsData = [...wordsData];
			updatedWordsData[currentImage].done = true;
			updatedWordsData[currentImage].blurred = 0;
			updatedWordsData[currentImage].allSlotsFilled = false;
			setWordsData(updatedWordsData);
		} else {
			alert("Wrong answer! Try again!");
			reduceMaxPoints();
		}
	};

	return (
		<div className="blurred-image-container">
			{wordsData.length > 0 && wordsData[currentImage].maxPoints && (
				<ScorePlate score={wordsData[currentImage].maxPoints} />
			)}

			{wordsData.length > 0 && wordsData[currentImage] && (
				<BlurredImage
					image={wordsData[currentImage].image}
					blur={
						wordsData[currentImage].done
							? wordsData[currentImage].blurred
							: blur
					}
				/>
			)}

			{!wordsData[currentImage]?.allSlotsFilled && (
				<Button
					disabled={wordsData[currentImage]?.done}
					icon={wordsData[currentImage]?.done ? <MdDone /> : <RiFocus2Line />}
					onClickHandler={handleImageBlur}
				/>
			)}
			{wordsData[currentImage]?.allSlotsFilled && (
				<button type="button" className="check-btn" onClick={checkAnswer}>
					Check It!
				</button>
			)}

			{wordsData.length > 0 && (
				<WordInput
					guessWord={wordsData[currentImage].name}
					onHint={hint}
					setAllSlotsFilled={setAllSlotsFilled}
					imageIsClear={imageIsClear}
					word={word}
					setWord={setWord}
					wordIsDone={wordsData[currentImage].done}
					currentImage={currentImage}
					wordsData={wordsData}
					reduceMaxPoints={reduceMaxPoints}
					setWordsData={setWordsData}
				/>
			)}

			<div className="prev-next-container">
				<Button icon={<FcPrevious />} onClickHandler={goToPrevWord} />
				<Button icon={<FcNext />} onClickHandler={goToNextWord} />
			</div>
		</div>
	);
};
